import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import ProspectDetailHeader from './components/ProspectDetailHeader/ProspectDetailHeader';
import PendingActivationSection from './sections/PendingActivationSection/PendingActivationSection';
import PendingQuoteSection from './sections/PendingQuoteSection/PendingQuoteSection';
import QuotedSection from './sections/QuotedSection/QuotedSection';
import { ReactComponent as FoldersIcon } from '../../../../assets/icons/folders.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import CustomButton from '../../../../shared/components/Buttons/CustomButton/CustomButton';
import EmptyState from '../../../../shared/components/Globals/EmptyState/EmptyState';
import ProspectActivationModal from '../../../../shared/components/Modals/ProspectActivationModal/ProspectActivationModal';
import { PROSPECTS_ROUTE } from '../../../../shared/constants/auth';
import { CLICK_ACTION, CONFIRM_ACTIVATION, SOURCE_PANEL } from '../../../../shared/constants/tagManager';
import { useProspectStatusUpdate } from '../../../../shared/hooks/useProspectStatusUpdate/useProspectStatusUpdate';
import { useUser } from '../../../../shared/hooks/useUser/useUser';
import type { IProspect } from '../../../../shared/interfaces/prospects';
import type { IQuote } from '../../../../shared/interfaces/quotes';
import QuotesService from '../../../../shared/services/api/quotes/quotes';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../../shared/translations/prospects';
import { ProspectStatus, type ProspectStatus as ProspectStatusType } from '../../../../shared/types/prospects';
import { excludeInactiveQuotes } from '../../../../shared/utils/quotes/quotes';
import './ProspectDetail.scss';

interface IProspectDetailProps {
  prospect: IProspect | null;
  isNew?: boolean;
  isNewUser?: boolean;
  statusFilter?: ProspectStatusType | null;
  onProspectStatusUpdated?: (newStatus: ProspectStatusType) => void;
  onProspectStatusUpdateError?: () => void;
  onActivationRequested?: (prospectId: number, companyName: string) => void;
  onNewProspectDismissed?: () => void;
  onNewProspect?: () => void;
}

const ProspectDetail = ({
  prospect,
  isNew = false,
  isNewUser = false,
  statusFilter,
  onProspectStatusUpdated = () => {},
  onProspectStatusUpdateError,
  onActivationRequested,
  onNewProspectDismissed,
  onNewProspect
}: IProspectDetailProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { brokerId } = useUser();
  const [quotes, setQuotes] = useState<IQuote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [isNewDismissed, setIsNewDismissed] = useState(false);
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);

  useEffect(() => {
    if (isNew) setIsNewDismissed(false);
  }, [isNew]);

  const fetchQuotes = useCallback(async (currentBrokerId: string, prospectId: number) => {
    try {
      setLoadingQuotes(true);
      const data = await QuotesService.getQuotes(currentBrokerId, prospectId);
      setQuotes(excludeInactiveQuotes(data));
    } catch {
      setQuotes([]);
    } finally {
      setLoadingQuotes(false);
    }
  }, []);

  useEffect(() => {
    if (prospect && brokerId) {
      if (prospect.status === ProspectStatus.Inactive || prospect.status === ProspectStatus.Lost) {
        setQuotes([]);
        return;
      }
      fetchQuotes(brokerId, prospect.id);
    } else {
      setQuotes([]);
    }
  }, [prospect, brokerId, fetchQuotes]);

  const getStatus = (): 'pending_quote' | 'quoted' | 'pending_activation' => {
    if (prospect?.status === 'pending_activation') return 'pending_activation';
    if (quotes.length > 0) return 'quoted';
    return 'pending_quote';
  };

  const handleViewProfile = (id: number) => {
    navigate(`${PROSPECTS_ROUTE}/${id}`);
  };

  const handleDismissNew = (_id: number) => {
    setIsNewDismissed(true);
    onNewProspectDismissed?.();
  };

  const handleRequestActivation = useCallback(
    (id: number) => {
      if (!prospect) return;
      onActivationRequested?.(id, prospect.companyName);
    },
    [prospect, onActivationRequested]
  );

  const handleConfirmActivation = () => {
    setIsActivationModalOpen(true);
    TagManagerService.pushEvent(CLICK_ACTION, CONFIRM_ACTIVATION, SOURCE_PANEL);
  };

  const handleCompleteFields = handleViewProfile;
  const {
    handleMarkAsLost: markAsLost,
    handleMarkAsInactive: markAsInactive,
    isUpdatingStatus
  } = useProspectStatusUpdate({
    brokerId: brokerId ?? undefined,
    prospectId: prospect?.id,
    currentStatus: prospect?.status,
    onSuccess: onProspectStatusUpdated,
    onError: onProspectStatusUpdateError
  });

  const handleMarkAsLost = useCallback((_id: number) => markAsLost(), [markAsLost]);
  const handleMarkAsInactive = useCallback((_id: number) => markAsInactive(), [markAsInactive]);

  if (!prospect) {
    if (isNewUser) {
      return (
        <div className="prospect-detail">
          <div className="prospect-detail__empty">
            <EmptyState
              icon={<FoldersIcon aria-hidden="true" />}
              title={t(TRANSLATIONS_PROSPECTS.emptyState.title)}
              description={t(TRANSLATIONS_PROSPECTS.emptyState.description)}
            >
              <CustomButton variant="primary" size="l" onClick={onNewProspect}>
                <PlusIcon className="prospects__new-prospect-icon" />
                {t(TRANSLATIONS_PROSPECTS.emptyState.newProspect)}
              </CustomButton>
            </EmptyState>
          </div>
        </div>
      );
    }

    if (statusFilter) {
      const statusLabel = t(TRANSLATIONS_PROSPECTS.status[statusFilter]);
      return (
        <div className="prospect-detail">
          <div className="prospect-detail__empty">
            <EmptyState
              icon={<FoldersIcon aria-hidden="true" />}
              title={t(TRANSLATIONS_PROSPECTS.emptyState.filterTitle, { status: statusLabel })}
              description={t(TRANSLATIONS_PROSPECTS.emptyState.filterDescription)}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="prospect-detail">
        <div className="prospect-detail__empty">{t(TRANSLATIONS_PROSPECTS.detail.selectProspect)}</div>
      </div>
    );
  }

  const currentStatus = getStatus();
  const isInactiveOrLost = prospect.status === ProspectStatus.Inactive || prospect.status === ProspectStatus.Lost;

  return (
    <div className="prospect-detail">
      <ProspectDetailHeader prospect={prospect} isLoading={loadingQuotes} onViewProfile={handleViewProfile} />

      {!isInactiveOrLost && (
        <div className="prospect-detail__content">
          {loadingQuotes ? (
            <div className="prospect-detail__section-skeleton" aria-hidden="true" />
          ) : (
            <>
              {currentStatus === 'pending_quote' && (
                <PendingQuoteSection
                  prospect={prospect}
                  isNew={isNew && !isNewDismissed}
                  onDismissNew={handleDismissNew}
                  onMarkAsLost={handleMarkAsLost}
                  onMarkAsInactive={handleMarkAsInactive}
                  isUpdatingStatus={isUpdatingStatus}
                />
              )}

              {currentStatus === 'quoted' && (
                <QuotedSection
                  prospect={prospect}
                  quotes={quotes}
                  onRequestActivation={handleRequestActivation}
                  onMarkAsLost={handleMarkAsLost}
                  onMarkAsInactive={handleMarkAsInactive}
                  isUpdatingStatus={isUpdatingStatus}
                />
              )}

              {currentStatus === 'pending_activation' && (
                <PendingActivationSection
                  prospect={prospect}
                  quotes={quotes}
                  onConfirmActivation={handleConfirmActivation}
                  onCompleteFields={handleCompleteFields}
                  onMarkAsLost={handleMarkAsLost}
                  onMarkAsInactive={handleMarkAsInactive}
                  isUpdatingStatus={isUpdatingStatus}
                />
              )}
            </>
          )}
        </div>
      )}

      <ProspectActivationModal
        open={isActivationModalOpen}
        prospect={prospect}
        onClose={() => setIsActivationModalOpen(false)}
      />
    </div>
  );
};

export default ProspectDetail;
